/** Assertion functions for runtime and type safety. */

export class AssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssertionError';
  }
}

/**
 * Assert that a value is defined.
 *
 * @example
 * const stringify = (value: number | undefined): number => {
 *  assertExists(value)
 *  return `${value}` // TS now knows that value is of type `number`
 * }
 */
export const assertExists: <T>(
  value: T,
  message: string
) => asserts value is NonNullable<T> = (value, message) => {
  if (value === null || value === undefined) {
    throw new AssertionError(message);
  }
};

/**
 * Throws a build errors for unexpected types in switch statement.
 *
 * @example
 * type MetaSyntactic = 'foo' | 'bar' | 'baz';
 *
 * let myVar: MetaSyntactic = 'foo';
 *
 * switch (myVar) {
 *   case 'foo':
 *   case 'bar':
 *     console.log('foo or bar');
 *     break;
 *   default:
 *     assertExhaustive(myVar); // Build error since 'baz' is not handled
 * }
 */
export const assertExhaustive = (value: never): never => {
  throw new Error(`Unchecked type: ${String(value)}`);
};

/**
 * Throws a build error for unexpected types in switch statement.
 * Logs an error if a case is not handled.
 *
 * @example
 * type MetaSyntactic = 'foo' | 'bar' | 'baz';
 *
 * let myVar: MetaSyntactic = 'foo';
 *
 * switch (myVar) {
 *   case 'foo':
 *   case 'bar':
 *     console.log('foo or bar');
 *     break;
 *   default:
 *     assumeExhaustive(myVar); // Build error since 'baz' is not handled
 * }
 */
export const assumeExhaustive = (value: never): void => {
  // eslint-disable-next-line no-console
  console.warn(`Unchecked type: ${String(value)}`);
};
